import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./createreservation.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import API from "src/API2";
import API3 from "src/API3";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcUser    = () => <Svg><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcBed     = () => <Svg><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcCash    = () => <Svg><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const IcCheck   = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcArrow   = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcClose   = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcUsers   = () => <Svg><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>;
const IcCalIn   = () => <Svg><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></Svg>;
const IcCalOut  = () => <Svg><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></Svg>;
const IcDoc     = () => <Svg size={14}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Svg>;

/* ── Helpers ────────────────────────────────────────────────────── */
const fmtMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " DA";
};
const nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  const start = new Date(a); start.setHours(0, 0, 0, 0);
  const end = new Date(b); end.setHours(0, 0, 0, 0);
  const diff = (end - start) / 86400000;
  return Number.isFinite(diff) && diff > 0 ? Math.round(diff) : 0;
};
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const CreateReservation = () => {
  const location = useLocation();
  const notification = location.state?.notification;
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [firstname, setFirstname] = useState(notification?.name || "");
  const [lastname, setLastname] = useState(notification?.surnom || "");
  const [birthDate, setBirthDate] = useState(
    notification?.applicants?.[0]?.birthday ? new Date(notification.applicants[0].birthday) : new Date()
  );
  const [rateId, setRateId] = useState(notification ? [notification.ratestr, notification.roomType] : []);
  const [arrivalTime, setArrivalTime] = useState(notification?.arrival ? notification.arrival : "11");
  const [email, setEmail] = useState(notification?.email || "");
  const [phone, setPhone] = useState(notification?.phone || "");
  const [nbAdult, setNbAdult] = useState(notification?.numAdults || 0);
  const [nbChildrens, setNbChildrens] = useState(notification?.numChildren || 0);
  const [startDate, setStartDate] = useState(notification?.checkinDate ? new Date(notification.checkinDate) : new Date());
  const [endtDate, setEndDate] = useState(notification?.checkoutDate ? new Date(notification.checkoutDate) : new Date());
  const [options, setOptions] = useState("");
  const [price, setPrice] = useState(notification?.totalPrice || "0");
  const [online, setOnline] = useState(true);
  const [roomi, setRoomi] = useState("");
  const [rate, setRate] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomCode, setSelectedRoomCode] = useState("");
  const [showApplicantsModal, setShowApplicantsModal] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomTypeName, setRoomTypeName] = useState("");

  const applicant = notification?.applicants || [];

  /* ── Data fetching (unchanged behavior) ── */
  useEffect(() => {
    toast.promise(
      API.get("rates").then((resp) => {
        setRate(resp.data.rates);
        if (notification) {
          const selectedRate = resp.data.rates.find((r) => r.id === notification.ratestr);
          if (selectedRate) {
            const roomDetail = selectedRate.details.find((d) => d.room.id === notification.roomType);
            if (roomDetail) setRoomTypeName(roomDetail.room.code);
          }
        }
      }),
      {
        success: "Rates loaded",
        loading: "Fetching rates…",
        error: "Could not fetch rates",
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const resp = await API.get("roomitems");
        const fetchedRooms = resp.data.rooms || [];
        const allRooms = fetchedRooms.sort((a, b) => a.number - b.number);
        setRooms(allRooms);

        const targetRoomType = notification?.roomType || rateId[1];
        if (targetRoomType) {
          const roomsOfType = allRooms.filter(
            (room) => room.hotelRoom?.id === targetRoomType || room.hotelRoom_id === targetRoomType,
          );
          setAvailableRooms(roomsOfType);
        } else {
          setAvailableRooms(allRooms);
        }
      } catch (err) {
        console.error("Error fetching rooms:", err);
      }
    };
    fetchRooms();
  }, [notification, rateId]);

  const handleStatusChange = async (id, status) => {
    try {
      await API.patch(`/notifications/${id}/status`, { status });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status } : n)));
      if (status === "COMPLETED") {
        const found = notifications.find((n) => n.id === id);
        navigate("/reservations/create", { state: { notification: found } });
      }
    } catch (err) {
      console.error(`Error updating notification status to ${status}:`, err);
    }
  };

  const post = async () => {
    const applicantJson = JSON.stringify(applicant);
    const payload = {
      nb_adults: parseInt(nbAdult),
      nb_children: parseInt(nbChildrens),
      checkin_date: startDate.toISOString(),
      checkout_date: endtDate.toISOString(),
      rate_id: rateId[0],
      room_id: rateId[1],
      firstname,
      lastname,
      email,
      phone: String(phone),
      arrival_time: String(arrivalTime),
      more: options,
      birthdate: birthDate.toISOString(),
      send_epaiment: online,
      price,
      roomi_id: roomi,
      applicant: applicantJson,
    };

    const handleReservation = async () => {
      try {
        const resp = await API.post("reservations/custom", payload);
        if (resp.error) throw new Error(resp.error);
        if (notification?.id) {
          await handleStatusChange(notification.id, "COMPLETED");
          await API3.put(`/hotelreservations/haccept/${notification?.hotel_id}`);
        }
        window.location.href = "/reservations";
        return resp;
      } catch (err) {
        console.error(err);
        throw err;
      }
    };

    toast.promise(handleReservation(), {
      success: "Reservation created",
      loading: "Creating reservation…",
      error: (err) =>
        err.response?.status === 400
          ? "The room is already booked for those dates"
          : "Failed to create reservation",
    });
  };

  const handleRateSelect = (e) => {
    const [newRateId, newRoomId] = e.target.value.split(":");
    setRateId([newRateId, newRoomId]);
    setRoomi("");
    const selectedRate = rate?.find((r) => r.id === newRateId);
    const selectedRoomDetail = selectedRate?.details.find((d) => d.room.id === newRoomId);
    setSelectedRoomCode(selectedRoomDetail?.room.code || "");
  };

  const toggleApplicantsModal = (e) => {
    if (e) e.preventDefault();
    setShowApplicantsModal(!showApplicantsModal);
  };

  /* ── Derived ── */
  const nights = useMemo(() => nightsBetween(startDate, endtDate), [startDate, endtDate]);
  const totalGuests = (Number(nbAdult) || 0) + (Number(nbChildrens) || 0);
  const guestName = `${firstname || "—"} ${lastname || ""}`.trim();
  const initials = ((firstname?.[0] || "") + (lastname?.[0] || "")).toUpperCase() || "?";

  return (
    <div className="cr-page">
      {/* ── Hero ── */}
      <header className="cr-hero">
        <button type="button" className="cr-back" onClick={() => navigate("/reservations")}>
          <IcArrow /> Back to reservations
        </button>
        <div className="cr-hero__body">
          <div>
            <span className="cr-hero__eyebrow">
              {notification ? "Confirming guest request" : "New booking"}
            </span>
            <h1 className="cr-hero__title">Create reservation</h1>
            <p className="cr-hero__sub">
              {notification
                ? "Review the request and assign the room to confirm this booking."
                : "Capture guest details, dates and payment to create a reservation."}
            </p>
          </div>
          {notification?.applicants?.length > 0 && (
            <button type="button" className="cr-btn cr-btn--ghost cr-btn--on-dark" onClick={toggleApplicantsModal}>
              <IcUsers /> Show applicants ({notification.applicants.length})
            </button>
          )}
        </div>
      </header>

      <div className="cr-grid">
        {/* ── Form column ── */}
        <div className="cr-form">
          {/* Section: Guest */}
          <section className="cr-card">
            <header className="cr-card__head">
              <span className="cr-card__icon"><IcUser /></span>
              <div>
                <h3>Guest information</h3>
                <p>Who is the booking for?</p>
              </div>
            </header>
            <div className="cr-grid-2">
              <label className="cr-field">
                <span>First name</span>
                <input value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder="Mohamed" />
              </label>
              <label className="cr-field">
                <span>Last name</span>
                <input value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder="Bakir" />
              </label>
              <label className="cr-field">
                <span>Birthdate</span>
                <DatePicker selected={birthDate} onChange={(d) => setBirthDate(d)} dateFormat="dd/MM/yyyy" />
              </label>
              <label className="cr-field">
                <span>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@example.com" />
              </label>
              <label className="cr-field cr-field--full">
                <span>Phone</span>
                <input
                  type="number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.valueAsNumber)}
                  placeholder="+213 …"
                />
              </label>
            </div>
          </section>

          {/* Section: Stay */}
          <section className="cr-card">
            <header className="cr-card__head">
              <span className="cr-card__icon cr-card__icon--orange"><IcBed /></span>
              <div>
                <h3>Stay details</h3>
                <p>Dates, occupancy and room assignment.</p>
              </div>
            </header>
            <div className="cr-grid-2">
              <label className="cr-field">
                <span>Adults</span>
                <input
                  type="number"
                  value={nbAdult}
                  min={0}
                  onChange={(e) => setNbAdult(e.target.valueAsNumber)}
                />
              </label>
              <label className="cr-field">
                <span>Children</span>
                <input
                  type="number"
                  value={nbChildrens}
                  min={0}
                  onChange={(e) => setNbChildrens(e.target.valueAsNumber)}
                />
              </label>
              <label className="cr-field">
                <span>Check-in</span>
                <DatePicker selected={startDate} onChange={(d) => setStartDate(d)} dateFormat="dd/MM/yyyy" />
              </label>
              <label className="cr-field">
                <span>Check-out</span>
                <DatePicker
                  selected={endtDate}
                  onChange={(d) => setEndDate(d)}
                  dateFormat="dd/MM/yyyy"
                  minDate={startDate}
                />
              </label>
              <label className="cr-field">
                <span>Arrival time</span>
                <input
                  value={arrivalTime}
                  onChange={(e) => setArrivalTime(e.target.value || "11")}
                  placeholder="11"
                />
              </label>
              <label className="cr-field">
                <span>Room type & rate</span>
                <select
                  value={
                    notification
                      ? `${notification.ratestr}:${notification.roomType}`
                      : rateId[0]
                      ? `${rateId[0]}:${rateId[1]}`
                      : ""
                  }
                  onChange={handleRateSelect}
                  disabled={!!notification}
                >
                  {notification ? (
                    <option value={`${notification.ratestr}:${notification.roomType}`}>
                      {roomTypeName || "Loading room type…"}
                    </option>
                  ) : (
                    <>
                      <option value="" disabled>Select room type</option>
                      {rate?.map((r) =>
                        r.details.map((detail) => (
                          <option key={`${r.id}:${detail.room.id}`} value={`${r.id}:${detail.room.id}`}>
                            {detail.room.code} · {r.code} ({fmtMoney(detail.price)})
                          </option>
                        )),
                      )}
                    </>
                  )}
                </select>
              </label>
              <label className="cr-field cr-field--full">
                <span>Assigned room</span>
                <select
                  value={roomi}
                  onChange={(e) => setRoomi(e.target.value)}
                  disabled={availableRooms.length === 0 || !rateId[1]}
                >
                  <option value="" disabled>
                    {!rateId[1]
                      ? "Select a room type first"
                      : availableRooms.length === 0
                      ? "No available rooms"
                      : "Pick an available room"}
                  </option>
                  {availableRooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      Room {room.number} · Floor {room.floor?.level ?? "N/A"}
                    </option>
                  ))}
                </select>
                {availableRooms.length === 0 && rateId[1] && (
                  <span className="cr-form-error">No available rooms for the selected type.</span>
                )}
              </label>
              <label className="cr-field cr-field--full">
                <span>Special requests</span>
                <textarea
                  rows={3}
                  value={options}
                  onChange={(e) => setOptions(e.target.value)}
                  placeholder="National card ID, extra bed, late check-in…"
                />
              </label>
            </div>
          </section>

          {/* Section: Payment */}
          <section className="cr-card">
            <header className="cr-card__head">
              <span className="cr-card__icon cr-card__icon--green"><IcCash /></span>
              <div>
                <h3>Payment</h3>
                <p>Total amount and payment-link delivery.</p>
              </div>
            </header>
            <div className="cr-grid-2">
              <label className="cr-field">
                <span>Total price (DA)</span>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="12000"
                />
              </label>
              <label className="cr-field">
                <span>Send e-payment link</span>
                <select value={String(online)} onChange={(e) => setOnline(e.target.value === "true")}>
                  <option value="true">Enabled — email link to guest</option>
                  <option value="false">Disabled — handle payment manually</option>
                </select>
              </label>
            </div>
          </section>
        </div>

        {/* ── Summary column ── */}
        <aside className="cr-summary">
          <div className="cr-summary__card">
            <header className="cr-summary__head">
              <span className="cr-summary__avatar">{initials}</span>
              <div>
                <span className="cr-summary__eyebrow">Reservation summary</span>
                <h3>{guestName || "New guest"}</h3>
                <span className="cr-summary__meta">{email || "—"}</span>
              </div>
            </header>

            <ul className="cr-summary__list">
              <li>
                <span><IcCalIn /> Check-in</span>
                <strong>{fmtDate(startDate)}</strong>
              </li>
              <li>
                <span><IcCalOut /> Check-out</span>
                <strong>{fmtDate(endtDate)}</strong>
              </li>
              <li>
                <span><IcDoc /> Nights</span>
                <strong>{nights}</strong>
              </li>
              <li>
                <span><IcUsers /> Guests</span>
                <strong>{totalGuests} ({nbAdult || 0} adt · {nbChildrens || 0} ch)</strong>
              </li>
              <li>
                <span><IcBed /> Room</span>
                <strong>{selectedRoomCode || roomTypeName || "—"}</strong>
              </li>
              <li>
                <span><IcCash /> E-payment</span>
                <strong>{online ? "Enabled" : "Disabled"}</strong>
              </li>
            </ul>

            <div className="cr-summary__total">
              <span>Total</span>
              <strong>{fmtMoney(price)}</strong>
            </div>

            <div className="cr-summary__actions">
              <button type="button" className="cr-btn cr-btn--ghost" onClick={() => navigate("/reservations")}>
                Cancel
              </button>
              <button type="button" className="cr-btn cr-btn--primary" onClick={post}>
                <IcCheck /> Create reservation
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* ── Applicants modal ── */}
      {showApplicantsModal && (
        <div className="cr-modal-overlay" onClick={toggleApplicantsModal}>
          <div className="cr-modal" onClick={(e) => e.stopPropagation()}>
            <header className="cr-modal__head">
              <div>
                <span className="cr-modal__eyebrow">Booking party</span>
                <h3>Applicants ({notification?.applicants?.length || 0})</h3>
              </div>
              <button className="cr-icon-btn" onClick={toggleApplicantsModal} aria-label="Close">
                <IcClose />
              </button>
            </header>
            <div className="cr-modal__body">
              {notification?.applicants?.map((a, i) => (
                <article key={i} className="cr-applicant">
                  <span className="cr-summary__avatar cr-summary__avatar--sm">
                    {((a.firstname?.[0] || "?") + (a.lastname?.[0] || "")).toUpperCase()}
                  </span>
                  <div className="cr-applicant__body">
                    <strong>{a.firstname} {a.lastname}</strong>
                    <span className="cr-applicant__meta">
                      {a.gender} · Born {a.birthday ? new Date(a.birthday).toLocaleDateString("fr-FR") : "—"}
                    </span>
                    <span className="cr-applicant__meta">
                      <IcDoc /> {a.documentType} · {a.documentNumber}
                    </span>
                    {a.additionalInfo && (
                      <span className="cr-applicant__note">{a.additionalInfo}</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateReservation;
